import React, { useEffect, useMemo, useState } from "react";

import CommonInput from "../../Components/Input";
import MultiSelect, { type Option } from "../../Components/MultiSelect";
import Select from "../../Components/Select";
import TextArea from "../../Components/TextArea";
import ToggleSwitch from "../../Components/ToggleSwitch";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  clearCreateAdminTipState,
  clearUpdateAdminTipState,
  createAdminTip,
  fetchAdminTips,
  updateAdminTip,
  type AdminTip,
} from "../../Store/Reducers/AdminTipsSlice";
import {
  ACCOUNT_TEAM_CATEGORY_ORDER,
  CATEGORY_LABELS,
  CATEGORY_ORDER,
  LEVEL_ORDER,
  PROGRAM_LEADER_CATEGORY_ORDER,
  SPONSOR_JOURNEY_BROWSE_KEY,
  SPONSOR_JOURNEY_CATEGORIES,
} from "../../Utils/sponsorTipsConstants";
import FormModal from "../FormModal";

interface TipFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  tip?: AdminTip | null;
  isEditMode?: boolean;
}

const AUDIENCE_OPTIONS = [
  { value: "sponsor", label: "Sponsor" },
  { value: "account_team", label: "Account team" },
  { value: "program_leader", label: "Program leader" },
];

const TIP_TYPE_OPTIONS = [
  { value: "best_practice", label: "Best practice" },
  { value: "failure_mode", label: "Failure mode" },
];

const DEFAULT_CATEGORY_BY_AUDIENCE: Record<string, string> = {
  sponsor: "relationship_fundamentals",
  account_team: "at_team_orientation",
  program_leader: "pl_program_foundation",
};

const LEVEL_OPTIONS = LEVEL_ORDER.filter(l => l !== "Unspecified").map(l => ({
  value: l,
  label: l === "ALL" ? "All levels" : l,
}));

const ROLE_LENS_OPTIONS = [
  { value: "", label: "None" },
  { value: "AE", label: "AE" },
  { value: "CSM", label: "CSM" },
  { value: "Both", label: "AE & CSM" },
];

const MAX_RELATED_TIPS = 4;

const getCategoryKeysForAudience = (audience: string): string[] => {
  if (audience === "account_team") {
    return [...ACCOUNT_TEAM_CATEGORY_ORDER];
  }
  if (audience === "program_leader") {
    return [...PROGRAM_LEADER_CATEGORY_ORDER];
  }
  return [
    ...CATEGORY_ORDER.filter(k => k !== SPONSOR_JOURNEY_BROWSE_KEY),
    ...SPONSOR_JOURNEY_CATEGORIES,
  ];
};

const toCategoryOptions = (keys: string[]) =>
  keys.map(value => ({
    value,
    label: CATEGORY_LABELS[value] || value,
  }));

const slugify = (value: string): string =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const emptyForm = () => ({
  title: "",
  url_slug: "",
  audience: "sponsor",
  category: DEFAULT_CATEGORY_BY_AUDIENCE.sponsor,
  tip_type: "best_practice",
  body: "",
  email_short: "",
  body_markdown: "",
  role_lens: "",
  xfactor_levels: ["ALL"] as string[],
  seniority: ["ALL"] as string[],
  related_tips: [] as string[],
  related_categories: [] as string[],
  paired_pitfall: "",
  active: true,
});

const TipFormModal: React.FC<TipFormModalProps> = ({
  isOpen,
  onClose,
  tip,
  isEditMode = false,
}) => {
  const dispatch = useAppDispatch();
  const {
    tips,
    createTipLoading,
    createTipSuccess,
    updateTipLoading,
    updateTipSuccess,
  } = useAppSelector(state => state.adminTips);

  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const categoryOptions = useMemo(
    () => toCategoryOptions(getCategoryKeysForAudience(formData.audience)),
    [formData.audience]
  );

  const audienceTipOptions = useMemo(
    () =>
      tips
        .filter(
          t =>
            t.audience === formData.audience &&
            (!isEditMode || !tip || t.id !== tip.id)
        )
        .map(t => ({ value: t.id, label: t.title })),
    [tips, formData.audience, isEditMode, tip]
  );

  const failureModeOptions = useMemo(() => {
    const items = tips
      .filter(
        t =>
          t.tip_type === "failure_mode" &&
          t.audience === formData.audience &&
          (!isEditMode || !tip || t.id !== tip.id)
      )
      .map(t => ({ value: t.id, label: t.title }));
    return [{ value: "", label: "Auto (week-based pairing)" }, ...items];
  }, [tips, formData.audience, isEditMode, tip]);

  const selectedRelatedTipOptions = useMemo((): Option[] => {
    const byId = new Map(audienceTipOptions.map(o => [o.value, o]));
    return formData.related_tips.map(
      id => byId.get(id) || { value: id, label: id }
    );
  }, [formData.related_tips, audienceTipOptions]);

  const selectedRelatedCategoryOptions = useMemo((): Option[] => {
    const byValue = new Map(categoryOptions.map(o => [o.value, o]));
    return formData.related_categories.map(
      id => byValue.get(id) || { value: id, label: CATEGORY_LABELS[id] || id }
    );
  }, [formData.related_categories, categoryOptions]);

  useEffect(() => {
    if (isOpen && tips.length === 0) {
      dispatch(fetchAdminTips({}) as any);
    }
  }, [isOpen, tips.length, dispatch]);

  useEffect(() => {
    if (isEditMode && tip) {
      setFormData({
        title: tip.title || "",
        url_slug: tip.url_slug || "",
        audience: tip.audience || "sponsor",
        category: tip.category || DEFAULT_CATEGORY_BY_AUDIENCE.sponsor,
        tip_type: tip.tip_type || "best_practice",
        body: tip.body || "",
        email_short: tip.email_short || "",
        body_markdown: tip.body_markdown || "",
        role_lens: tip.role_lens || "",
        xfactor_levels: tip.xfactor_levels?.length
          ? tip.xfactor_levels
          : ["ALL"],
        seniority: tip.seniority?.length ? tip.seniority : ["ALL"],
        related_tips: tip.related_tips || [],
        related_categories: tip.related_categories || [],
        paired_pitfall: tip.paired_pitfall || "",
        active: tip.active ?? true,
      });
    } else {
      setFormData(emptyForm());
    }
    setErrors({});
  }, [isEditMode, tip, isOpen]);

  useEffect(() => {
    if (createTipSuccess || updateTipSuccess) {
      onClose();
      dispatch(clearCreateAdminTipState());
      dispatch(clearUpdateAdminTipState());
    }
  }, [createTipSuccess, updateTipSuccess, dispatch, onClose]);

  const handleAudienceChange = (audience: string) => {
    setFormData(prev => {
      const keys = getCategoryKeysForAudience(audience);
      const category = keys.includes(prev.category)
        ? prev.category
        : DEFAULT_CATEGORY_BY_AUDIENCE[audience] || keys[0];
      const validRelated = prev.related_tips.filter(id =>
        tips.some(
          t => t.id === id && t.audience === audience && t.id !== tip?.id
        )
      );
      const pairedStillValid =
        !prev.paired_pitfall ||
        tips.some(
          t =>
            t.id === prev.paired_pitfall &&
            t.tip_type === "failure_mode" &&
            t.audience === audience
        );
      const validRelatedCategories = prev.related_categories.filter(c =>
        keys.includes(c)
      );
      return {
        ...prev,
        audience,
        category,
        role_lens: audience === "account_team" ? prev.role_lens : "",
        related_tips: validRelated,
        related_categories: validRelatedCategories,
        paired_pitfall: pairedStillValid ? prev.paired_pitfall : "",
      };
    });
  };

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!formData.title.trim()) next.title = "Title is required";
    if (!formData.url_slug.trim()) next.url_slug = "URL slug is required";
    if (!formData.body.trim()) next.body = "Body is required";
    const validCategories = getCategoryKeysForAudience(formData.audience);
    if (!validCategories.includes(formData.category)) {
      next.category = "Category is not valid for the selected audience";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const payload = {
      title: formData.title.trim(),
      url_slug: formData.url_slug.trim(),
      audience: formData.audience,
      category: formData.category,
      tip_type: formData.tip_type,
      body: formData.body.trim(),
      email_short: formData.email_short.trim() || formData.body.trim(),
      body_markdown: formData.body_markdown.trim() || null,
      role_lens:
        formData.audience === "account_team" && formData.role_lens
          ? formData.role_lens
          : null,
      xfactor_levels: formData.xfactor_levels,
      seniority: formData.seniority,
      active: formData.active,
      related_tips: formData.related_tips,
      related_categories:
        formData.tip_type === "failure_mode" ? formData.related_categories : [],
      paired_pitfall:
        formData.tip_type === "best_practice" && formData.paired_pitfall
          ? formData.paired_pitfall
          : null,
    };

    if (isEditMode && tip) {
      await dispatch(updateAdminTip({ tip_id: tip.id, ...payload }));
    } else {
      await dispatch(createAdminTip(payload));
    }
  };

  const toggleMulti = (
    field: "xfactor_levels" | "seniority",
    value: string
  ) => {
    setFormData(prev => {
      const current = prev[field];
      if (value === "ALL") {
        return { ...prev, [field]: ["ALL"] };
      }
      const withoutAll = current.filter(v => v !== "ALL");
      const next = withoutAll.includes(value)
        ? withoutAll.filter(v => v !== value)
        : [...withoutAll, value];
      return { ...prev, [field]: next.length ? next : ["ALL"] };
    });
  };

  const loading = isEditMode ? updateTipLoading : createTipLoading;
  const isFormValid =
    formData.title.trim() !== "" &&
    formData.url_slug.trim() !== "" &&
    formData.body.trim() !== "";

  const showRoleLens = formData.audience === "account_team";
  const showXfactorLevels = formData.audience === "sponsor";

  return (
    <FormModal
      isOpen={isOpen}
      toggle={onClose}
      title={isEditMode ? "Edit Tip" : "Add Tip"}
      onSubmit={handleSubmit}
      submitLabel={isEditMode ? "Update Tip" : "Create Tip"}
      isEditMode={isEditMode}
      loading={loading}
      disabled={!isFormValid}
    >
      <div>
        <CommonInput
          label="Title"
          name="title"
          value={formData.title}
          placeholder="Enter tip title"
          onChange={e => {
            const title = e.target.value;
            setFormData(prev => ({
              ...prev,
              title,
              url_slug:
                !isEditMode && !prev.url_slug ? slugify(title) : prev.url_slug,
            }));
          }}
          error={errors.title}
          required
        />
        <CommonInput
          label="URL slug"
          name="url_slug"
          value={formData.url_slug}
          placeholder="tip-url-slug"
          onChange={e =>
            setFormData(prev => ({ ...prev, url_slug: e.target.value }))
          }
          error={errors.url_slug}
          required
        />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4">
          <Select
            label="Audience"
            name="audience"
            value={formData.audience}
            onChange={e => handleAudienceChange(e.target.value)}
            options={AUDIENCE_OPTIONS}
            isShowPlaceHolder={false}
          />
          <Select
            label="Category"
            name="category"
            value={formData.category}
            onChange={e =>
              setFormData(prev => ({ ...prev, category: e.target.value }))
            }
            options={categoryOptions}
            isShowPlaceHolder={false}
            error={errors.category}
          />
          <Select
            label="Tip type"
            name="tip_type"
            value={formData.tip_type}
            onChange={e =>
              setFormData(prev => ({ ...prev, tip_type: e.target.value }))
            }
            options={TIP_TYPE_OPTIONS}
            isShowPlaceHolder={false}
          />
        </div>
        <TextArea
          label="Body"
          name="body"
          value={formData.body}
          placeholder="Enter the full tip content"
          onChange={e =>
            setFormData(prev => ({ ...prev, body: e.target.value }))
          }
          rows={5}
          error={errors.body}
          required
        />
        <TextArea
          label="Email short (optional)"
          name="email_short"
          value={formData.email_short}
          placeholder="Shorter version for tip"
          onChange={e =>
            setFormData(prev => ({ ...prev, email_short: e.target.value }))
          }
          rows={3}
        />
        {showRoleLens && (
          <Select
            label="Role lens (optional)"
            name="role_lens"
            value={formData.role_lens}
            onChange={e =>
              setFormData(prev => ({ ...prev, role_lens: e.target.value }))
            }
            options={ROLE_LENS_OPTIONS}
            isShowPlaceHolder={false}
          />
        )}
        {showXfactorLevels && (
          <div className="mb-5">
            <span className="block mb-2 text-sm font-medium text-gray-900">
              XFACTOR Account Levels
            </span>
            <div className="flex flex-wrap gap-2">
              {LEVEL_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => toggleMulti("xfactor_levels", opt.value)}
                  className={`rounded-full px-3 py-1 text-xs font-medium border ${
                    formData.xfactor_levels.includes(opt.value)
                      ? "border-purple-600 bg-purple-50 text-purple-700"
                      : "border-gray-200 bg-white text-gray-600"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="mb-7">
          <MultiSelect
            label="Related tips (optional)"
            options={audienceTipOptions}
            selected={selectedRelatedTipOptions}
            onChange={selected =>
              setFormData(prev => ({
                ...prev,
                related_tips: selected
                  .slice(0, MAX_RELATED_TIPS)
                  .map(s => s.value),
              }))
            }
            placeholder="Search tips to link (max 4)"
            showAvatar={false}
          />
          <p className="mt-2 text-xs text-gray-500">
            Shown first in &quot;More like this&quot; on the tip detail page.
          </p>
        </div>
        {formData.tip_type === "best_practice" && (
          <Select
            label="Paired failure mode (optional)"
            name="paired_pitfall"
            value={formData.paired_pitfall}
            onChange={e =>
              setFormData(prev => ({
                ...prev,
                paired_pitfall: e.target.value,
              }))
            }
            options={failureModeOptions}
            isShowPlaceHolder={false}
            infoText="Leave on auto to rotate by week when no explicit pitfall is set."
          />
        )}
        {formData.tip_type === "failure_mode" && (
          <div className="mb-7">
            <MultiSelect
              label="Related categories (optional)"
              options={categoryOptions}
              selected={selectedRelatedCategoryOptions}
              onChange={selected =>
                setFormData(prev => ({
                  ...prev,
                  related_categories: selected.map(s => s.value),
                }))
              }
              placeholder="Categories for best-practice pairing"
              showAvatar={false}
            />
            <p className="mt-2 text-xs text-gray-500">
              Used when pairing this failure mode to a best practice on the
              detail page.
            </p>
          </div>
        )}
        <div className="flex items-center justify-between gap-4 mt-1">
          <div>
            <p className="text-sm font-semibold text-gray-900 m-0">Active</p>
            <p className="text-xs text-gray-500 mt-0.5 mb-0">
              Visible in the Guidance Library for all tenants
            </p>
          </div>
          <ToggleSwitch
            checked={formData.active}
            onChange={checked =>
              setFormData(prev => ({ ...prev, active: checked }))
            }
            id="tip-form-is-active"
          />
        </div>
      </div>
    </FormModal>
  );
};

export default TipFormModal;
